import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, firstValueFrom, catchError, of } from 'rxjs';
import { UserService } from '../services/user.service';

export interface FeatureFlags {
	[featureName: string]: boolean;
}

export interface FeatureValues {
	[featureName: string]: string;
}

export interface AppFeatureConfig {
	features: FeatureFlags;
	values: FeatureValues;
}


@Injectable({
	providedIn: 'root'
})
export class FeatureService {

	private userEventSubscription!: Subscription;

	private DEFAULT_CUSTOMER: string = 'default';
	private customerId!: string;
	private linkIdCache: string[] = [];

	// AppFeatureConfig contains the custom featureset for a customer
	private config: AppFeatureConfig | null = null;

	constructor(
		private http: HttpClient,
		private userService: UserService
	) {
		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if ('loggedin' === event) {
				this.loadCustomerUserStylesheet();
			}
			else if ('loggedout' === event) {
				this.removeCustomerUserStylesheet();
			}
		});
	}

	ngOnDestroy() {
		this.userEventSubscription.unsubscribe();
	}

	async loadFeatureConfig(): Promise<AppFeatureConfig | null> {
		if (!this.config) {
			this.customerId = this.getCustomerId();

			const url = `/app/aw-ui-angular/assets/config/features/${this.customerId}.json`;

			try {
				this.config = await firstValueFrom(this.http.get<AppFeatureConfig>(url, {}));
				this.loadCustomerStylesheet();
			} catch (error) {
				this.config = {
					'features': {},
					'values': {}
				};

				console.warn("Unable to load features config file. Using default features configuration.");
			}
		}

		console.log(`Loaded feature configuration for customer: ${this.customerId}`);
		console.log(this.config);

		return this.config;
	}

	private loadCustomerStylesheet(): void {
		if (!this.customerId || this.customerId === this.DEFAULT_CUSTOMER) {
			return;
		}

		const cssHref = `/app/aw-ui-angular/assets/custom/${this.customerId}/css/main-custom.css`;

		if (!document.querySelector(`link[href="${cssHref}"]`)) {
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = cssHref;
			link.id = `custom-style-${this.customerId}`;
			document.head.appendChild(link);
		}
	}

	private loadCustomerUserStylesheet(): void {
		if (!this.customerId || this.customerId === this.DEFAULT_CUSTOMER) {
			return;
		}

		let username = this.userService.user.username ?? '';
		if (!username) {
			return;
		}

		const cssHref = `/app/aw-ui-angular/assets/custom/${this.customerId}/css/main-custom-${username}.css`;

		if (document.querySelector(`link[href="${cssHref}"]`)) {
			return;
		}

		// Attempt to load via HEAD
		this.http.head(cssHref, { observe: 'response' }).pipe(
			catchError((err: any) => {
				// Suppress error
				return of(null);
			})
		).subscribe((response: any) => {
			if (response && response.status === 200) {
				let linkId = `custom-style-${this.customerId}-${username}`;

				const link = document.createElement('link');
				link.rel = 'stylesheet';
				link.href = cssHref;
				link.id = linkId;
				document.head.appendChild(link);

				if (!this.linkIdCache.includes(linkId)) {
					this.linkIdCache.push(linkId);
				}
			} else {
				console.warn(`Stylesheet not found: ${cssHref}`);
			}
		});
	}

	private removeCustomerUserStylesheet() {
		for (let i = 0; i < this.linkIdCache.length; i++) {
			let cssElement = document.getElementById(this.linkIdCache[i]);
			if (cssElement) {
				cssElement.remove();
			}
		}
	}

	isFeatureEnabled(feature: string): boolean {
		return this.config?.features[feature] || false;
	}

	getFeatureValue(feature: string): string | null {
		return this.config?.values[feature] ?? null;
	}

	getFeatures(): FeatureFlags {
		return this.config?.features ?? {};
	}

	getCustomerId(): string {
		// See index.html for the window.customerConfig property
		return (window as any).customerConfig?.customerId || this.DEFAULT_CUSTOMER;
	}
}
